import { useMemo, useState, useEffect } from "react";
import useSWR from "swr";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { IconFileText, IconLink, IconPlus, IconSearch, IconDownload } from "@tabler/icons-react";
import { Badge, Box, Button, Divider, Drawer, Flex, Group, Modal, Pagination, Paper, Select, Stack, Tabs, Text, TextInput } from "@mantine/core";
import { DateInput } from "@mantine/dates";
import dayjs from "dayjs";
import { fetcher, multiFormatUploader } from "../../../../services/fetcher";
import {
  successNotifications,
  errorNotifications,
} from "../../../../utils/handleNotifications";
import EditableTable from "../../components/EditableTable/EditableTable";
import BookingStats from "../../components/BookingStats";
import { BookingStatus, BookingPriority, PaymentStatus, BookingReceived, InsuranceRepresentatives } from "../../utils/bookingOptions";
import { BOOKINGS_API } from "../../../../api/endpoints";
import  styles  from './Bookings.module.css'

const parseDateCell = (v) => {
  if (!v) return null;
  const d = v instanceof Date ? v : new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
};
const fmtTripCell = (start, end) => {
  const a = parseDateCell(start);
  const b = parseDateCell(end);
  if (!a || !b) return "—";
  const o = { day: "numeric", month: "short", year: "numeric" };
  return `${a.toLocaleDateString("en-GB", o)} – ${b.toLocaleDateString("en-GB", o)}`;
};

const statusBadgeColor = (s) => {
  const k = String(s || "").toUpperCase().replace(/\s+/g, "_");
  if (k === "OPEN") return "orange";
  if (k === "CLOSED") return "green";
  if (k === "IN_PROGRESS" || k === "IN_PROCESS") return "violet";
  if (k === "CANCELED") return "red";
  return "gray";
};

const priorityBadgeColor = (s) => {
  const k = String(s || "").toUpperCase();
  if (k === "HIGH") return "red";
  if (k === "MEDIUM") return "orange";
  if (k === "LOW") return "teal";
  return "gray";
};

const renderStatusBadge = (val) => (
  <Badge variant="light" color={statusBadgeColor(val)} size="sm" radius="xl" fw={600} tt="uppercase">
    {String(val || "—").replaceAll("_", " ")}
  </Badge>
);

const renderPriorityBadge = (val) => (
  <Badge variant="light" color={priorityBadgeColor(val)} size="sm" radius="xl" fw={600} tt="uppercase">
    {String(val || "—")}
  </Badge>
);

const EMBEDDED_BOOKING_DRAWER_FORM = [
  { key: "plan_name", label: "PLAN", kind: "readonly" },
  { key: "event_location", label: "LOCATION", kind: "text" },
  { key: "pax", label: "PAX", kind: "text" },
  { key: "start_date", label: "START DATE", kind: "date" },
  { key: "end_date", label: "END DATE", kind: "date" },
  { key: "days", label: "DAYS", kind: "text" },
  { key: "premium_amount", label: "PREMIUM", kind: "text" },
  { key: "payment_status", label: "PAYMENT STATUS", kind: "select", selectKind: "payment" },
  { key: "booking_received", label: "BOOKING RECEIVED", kind: "select", selectKind: "received" },
  { key: "sales_agent_name", label: "SALES AGENT", kind: "readonly" },
  { key: "issuance_representative_name", label: "INSURANCE REPRESENTATIVE", kind: "select", selectKind: "representative" },
  { key: "document_status", label: "DOCUMENT STATUS", kind: "text" },
  { key: "transaction_number", label: "TRANSACTION NUMBER", kind: "text" },
  { key: "remarks", label: "Remark", kind: "text" },
  { key: "created_on_display", label: "CREATED ON", kind: "readonly" },

];

const toFilterOptions = (allLabel, options, labelMap = {}) => [
  { value: "", label: allLabel },
  ...options.map((opt) => ({
    value: opt.value,
    label: labelMap[opt.value] || opt.label,
  })),
];

const STATUS_FILTER_OPTIONS = toFilterOptions("All Status", BookingStatus);
const PRIORITY_FILTER_OPTIONS = toFilterOptions("All Priority", BookingPriority);
const PAYMENT_FILTER_OPTIONS = toFilterOptions("All Payment", PaymentStatus, {
  PENDING: "Pending",
  RECEIVED: "Received",
  PAID_THROUGH_WEB: "Paid via Web",
});

const Bookings = ({ hideRowSelection = false }) => {
  const navigate = useNavigate();
  const userRole = useSelector((state) => state.auth.role);
  const [page, setPage] = useState(1);
  const [modalOpened, setModalOpened] = useState(false);
  const [activeTab, setActiveTab] = useState("SALES_TEAM");
  const [rowsPerPage, setRowsPerPage] = useState("25");
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [priorityFilter, setPriorityFilter] = useState("");
  const [paymentFilter, setPaymentFilter] = useState("");
  const [bookingDrawerOpened, setBookingDrawerOpened] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [drawerSaveLoading, setDrawerSaveLoading] = useState(false);

  const pageSize = Number(rowsPerPage) || 25;
  const getBookingsUrl = (tab, currentPage, size) =>
    `${BOOKINGS_API}?purchase_method=${tab}&page=${currentPage}&page_size=${size}`;

  const { data, mutate } = useSWR(
    () => getBookingsUrl(activeTab, page, pageSize),
    fetcher,
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    }
  );

  const currentBookings = useMemo(
    () => data?.results?.bookings || [],
    [data?.results?.bookings]
  );
  const totalBookings = data?.count || 0;
  const totalPages = Math.max(1, Math.ceil(totalBookings / pageSize));
  const showingFrom = totalBookings ? (page - 1) * pageSize + 1 : 0;
  // const openCount = currentBookings.filter((item) => item?.booking_status === "OPEN").length;
  // const inProgressCount = currentBookings.filter(
  //   (item) => item?.booking_status === "IN_PROGRESS" || item?.booking_status === "IN PROCESS"
  // ).length;
  // const pendingPaymentCount = currentBookings.filter(
  //   (item) => item?.payment_status === "PENDING"
  // ).length;
  // const premiumMtd = currentBookings.reduce(
  //   (sum, item) => sum + Number(item?.premium_amount || 0),
  //   0
  // );
  // const discountMtd = currentBookings.reduce(
  //   (sum, item) => sum + Number(item?.discount || 0),
  //   0
  // );
  const tabCounts = {
    SALES_TEAM:
      data?.results?.sales_team_count ??
      data?.results?.purchase_method_counts?.SALES_TEAM ??
      (activeTab === "SALES_TEAM" ? totalBookings : 0),
    WEBSITE:
      data?.results?.website_count ??
      data?.results?.purchase_method_counts?.WEBSITE ??
      (activeTab === "WEBSITE" ? totalBookings : 0),
    OPERATOR:
      data?.results?.operator_count ??
      data?.results?.purchase_method_counts?.OPERATOR ??
      (activeTab === "OPERATOR" ? totalBookings : 0),
  };

  const filteredBookings = useMemo(() => {
    let rows = currentBookings;
    if (statusFilter) {
      rows = rows.filter((item) => item?.booking_status === statusFilter);
    }
    if (priorityFilter) {
      rows = rows.filter((item) => item?.priority === priorityFilter);
    }
    if (paymentFilter) {
      rows = rows.filter((item) => item?.payment_status === paymentFilter);
    }
    const query = searchQuery.trim().toLowerCase();
    if (query) {
      rows = rows.filter((item) =>
        [item?.booking_no, item?.client_or_operator, item?.plan_name, item?.sales_agent_name, item?.email]
          .filter(Boolean)
          .map((val) => String(val).toLowerCase())
          .some((val) => val.includes(query))
      );
    }
    return rows;
  }, [currentBookings, statusFilter, priorityFilter, paymentFilter, searchQuery]);

  const displayFrom = filteredBookings.length ? showingFrom : 0;
  const showingTo = filteredBookings.length ? showingFrom + filteredBookings.length - 1 : 0;

  useEffect(() => {
    setPage(1);
  }, [activeTab, rowsPerPage]);

  useEffect(() => {
    if (hideRowSelection) return;
    setSelectedBooking(null);
  }, [statusFilter, priorityFilter, paymentFilter, searchQuery, activeTab, page, hideRowSelection]);

  const handleClearFilters = () => {
    setSearchQuery("");
    setStatusFilter("");
    setPriorityFilter("");
    setPaymentFilter("");
  };

  const compactColumns = [
    {
      key: "booking_no",
      label: "BOOKING NO.",
      render: (val) => (
        <Text size="sm" fw={600} c="#092C4C">
          {val || "—"}
        </Text>
      ),
    },
    { key: "booking_status", label: "STATUS", render: renderStatusBadge },
    { key: "priority", label: "PRIORITY", render: renderPriorityBadge },
    { key: "sales_agent_name", label: "SALES AGENT" },
    {
      key: "csv_file",
      label: "Uploaded CSV File",
      render: (val) =>
        val ? (
          <Button
            component="a"
            href={val}
            target="_blank"
            rel="noreferrer"
            download
            size="xs"
            variant="default"
            leftSection={<IconDownload size={12} />}
          >
            Download
          </Button>
        ) : (
          <Text size="sm">—</Text>
        ),
    },
    {
      key: "simple_csv",
      label: "Improved CSV File",
      render: (val) =>
        val ? (
          <Button
            component="a"
            href={val}
            target="_blank"
            rel="noreferrer"
            download
            size="xs"
            variant="default"
            leftSection={<IconDownload size={12} />}
          >
            Download
          </Button>
        ) : (
          <Text size="sm">—</Text>
        ),
    },
    { key: "client_or_operator", label: "CLIENT" },
    { key: "plan_name", label: "PLAN" },
    { key: "issuance_representative_name", label: "INSURANCE REPRESENTATIVE" },
    { key: "trip_date_display", label: "TRIP DATE" },
  ];

  const compactRows = useMemo(
    () =>
      filteredBookings.map((row) => ({
        ...row,
        trip_date_display: fmtTripCell(row.start_date, row.end_date),
      })),
    [filteredBookings]
  );

  const handleInputChange = async (rowId, field, value, isFile = false) => {
    try {
      const payload = isFile ? value : { [field]: value };
      await multiFormatUploader(`${BOOKINGS_API}${rowId}/`, "PATCH", payload);
      mutate(); // re-fetch current tab data
      successNotifications(`Updated ${field}`);
    } catch (err) {
      errorNotifications(err.response?.data || "Failed to update", err);
    }
  };

  const handleCloseBookingDrawer = () => {
    setBookingDrawerOpened(false);
    setSelectedBooking(null);
  };

  const handleBookingRowClick =
    hideRowSelection
      ? (row) => {
          setSelectedBooking(row?.booking || row);
          setBookingDrawerOpened(true);
        }
      : undefined;

  const handleSaveBooking = async () => {
    if (!selectedBooking?.id) return;
    setDrawerSaveLoading(true);
    try {
      const patchFields = [
        "plan_name",
        "event_location",
        "pax",
        "start_date",
        "end_date",
        "days",
        "premium_amount",
        "payment_status",
        "booking_received",
        "sales_agent_name",
        "issuance_representative_name",
        "document_status",
        "transaction_number",
        "booking_type",
        "purchase_method",
        "purchase_type",
        "client_or_operator",
        "booking_no",
        "user_email",
      ];

      const payload = patchFields.reduce((acc, key) => {
        if (selectedBooking?.[key] !== undefined) {
          acc[key] = selectedBooking[key];
        }
        return acc;
      }, {});

      await multiFormatUploader(`${BOOKINGS_API}${selectedBooking.id}/`, "PATCH", payload);
      await mutate();
      successNotifications("Booking saved");
    } catch (err) {
      errorNotifications(err.response?.data || "Failed to save booking", err);
    } finally {
      setDrawerSaveLoading(false);
    }
  };

  const setBookingDrawerField = (key, value) => {
    setSelectedBooking((prev) => (prev ? { ...prev, [key]: value } : prev));
  };

  const embedSelectData = (selectKind) => {
    if (selectKind === "payment") return PaymentStatus;
    if (selectKind === "received") return BookingReceived;
    if (selectKind === "representative") return InsuranceRepresentatives;
    return [];
  };

  const renderBookingDrawerValue = (fld) => {
    const rawValue = selectedBooking?.[fld.key];
    let value = rawValue;

    if (fld.key === "created_on_display") {
      const created = parseDateCell(selectedBooking?.created_on);
      value = created
        ? created.toLocaleString("en-IN", {
            day: "numeric",
            month: "numeric",
            year: "numeric",
            hour: "numeric",
            minute: "2-digit",
            second: "2-digit",
            hour12: true,
          })
        : "";
    }

    if (fld.kind === "readonly") {
      return (
        <TextInput
          size="xs"
          variant="default"
          value={value || ""}
          readOnly
          styles={{ input: { fontWeight: 600 } }}
        />
      );
    }

    if (fld.kind === "text") {
      return (
        <TextInput
          size="xs"
          variant="default"
          value={value || ""}
          onChange={(e) => setBookingDrawerField(fld.key, e.currentTarget.value)}
          styles={{ input: { fontWeight: 600 } }}
        />
      );
    }

    if (fld.kind === "date") {
      return (
        <DateInput
          size="xs"
          variant="default"
          value={parseDateCell(value)}
          onChange={(date) => setBookingDrawerField(fld.key, date ? dayjs(date).format("YYYY-MM-DD") : "")}
          valueFormat="YYYY-MM-DD"
          styles={{ input: { fontWeight: 600 } }}
        />
      );
    }

    if (fld.kind === "select") {
      return (
        <Select
          size="xs"
          variant="default"
          data={embedSelectData(fld.selectKind)}
          value={value || null}
          onChange={(val) => setBookingDrawerField(fld.key, val || "")}
          styles={{ input: { fontWeight: 600 } }}
        />
      );
    }

    return (
      <TextInput
        size="xs"
        variant="default"
        value={value || ""}
        onChange={(e) => setBookingDrawerField(fld.key, e.currentTarget.value)}
        styles={{ input: { fontWeight: 600 } }}
      />
    );
  };

  return (
    <Stack gap="md">
      <BookingStats />
      <Paper p="sm" radius="md" withBorder className={styles.filterBar}>
        <Flex justify="space-between" align="center" wrap="wrap" gap="sm">
          <Group gap="sm" wrap="wrap">
            <TextInput
              placeholder="Search booking / client..."
              leftSection={<IconSearch size={14} />}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              w={220}
            />
            <Select
              placeholder="All Status"
              data={STATUS_FILTER_OPTIONS}
              value={statusFilter || null}
              onChange={(value) => setStatusFilter(value || "")}
              w={140}
              allowDeselect={false}
            />
            <Select
              placeholder="All Priority"
              data={PRIORITY_FILTER_OPTIONS}
              value={priorityFilter || null}
              onChange={(value) => setPriorityFilter(value || "")}
              w={140}
              allowDeselect={false}
            />
            <Select
              placeholder="All Payment"
              data={PAYMENT_FILTER_OPTIONS}
              value={paymentFilter || null}
              onChange={(value) => setPaymentFilter(value || "")}
              w={150}
              allowDeselect={false}
            />
             <Button variant="subtle" size="compact-sm" onClick={handleClearFilters}>
            Clear
          </Button>
          </Group> 
          <Button variant="gradient" leftSection={<IconPlus size={14} />} onClick={() => setModalOpened(true)}>
            New booking
          </Button>
        </Flex>
      </Paper>

      <Tabs value={activeTab} onChange={setActiveTab} className={styles.container}>
          <Tabs.List className={styles.tabsList}>
            <Tabs.Tab value="SALES_TEAM">
              <Group gap={6}>
                <span>Sales team</span>
                <Badge size="xs" radius="xl" variant="light">{tabCounts.SALES_TEAM}</Badge>
              </Group>
            </Tabs.Tab>
            <Tabs.Tab value="WEBSITE">
              <Group gap={6}>
                <span>Website</span>
                <Badge size="xs" radius="xl" variant="light">{tabCounts.WEBSITE}</Badge>
              </Group>
            </Tabs.Tab>
            <Tabs.Tab value="OPERATOR">
              <Group gap={6}>
                <span>Operator</span>
                <Badge size="xs" radius="xl" variant="light">{tabCounts.OPERATOR}</Badge>
              </Group>
            </Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value={activeTab} className={styles.tabsPanel}>
            <EditableTable
              columns={compactColumns}
              data={hideRowSelection ? compactRows : filteredBookings}
              onChange={handleInputChange}
              role={userRole}
              compact={hideRowSelection}
              onRowClick={handleBookingRowClick}
              doubleClickRow={Boolean(handleBookingRowClick)}
            />
          </Tabs.Panel>
          <Flex justify="space-between" align="center" mt="md" className={styles.footerBar}>
            <Text className={styles.footerMeta}>
              Showing {displayFrom}–{showingTo} of {totalBookings} bookings
            </Text>
            <Group gap="xs" align="center">
              <Text className={styles.footerMeta}>Rows</Text>
              <Select
                size="xs"
                w={84}
                value={rowsPerPage}
                onChange={(value) => setRowsPerPage(value || "25")}
                data={[
                  { value: "10", label: "10" },
                  { value: "25", label: "25" },
                  { value: "50", label: "50" },
                  { value: "100", label: "100" },
                ]}
              />
              <Pagination
                total={totalPages}
                value={page}
                onChange={setPage}
                radius="sm"
                size="sm"
                siblings={1}
                withEdges
              />
            </Group>
          </Flex>

        </Tabs>

      {hideRowSelection ? (
        <Drawer
          opened={bookingDrawerOpened}
          onClose={handleCloseBookingDrawer}
          position="right"
          size="45%"
          withCloseButton
          title={
            <Stack gap={6}>
              <Text size="xs" c="dimmed">
                {selectedBooking?.booking_no || "—"}
              </Text>
              <Text fw={700} size="lg">
                Booking Detail
              </Text>
              <Group gap="xs">
                <Badge variant="light" color={statusBadgeColor(selectedBooking?.booking_status)} size="sm" radius="sm">
                  {String(selectedBooking?.booking_status || "—").replaceAll("_", " ")}
                </Badge>
                <Badge variant="light" color="gray" size="sm" radius="sm">
                  {String(selectedBooking?.booking_type || "—").replaceAll("_", " ")}
                </Badge>
              </Group>
              {selectedBooking?.csv_file ? (
                <Button
                  component="a"
                  href={selectedBooking.csv_file}
                  target="_blank"
                  rel="noreferrer"
                  download
                  size="xs"
                  variant="gradient"
                  leftSection={<IconDownload size={12} />}
                >
                  Download CSV
                </Button>
              ) : null}
            </Stack>
          }
        >
          <Stack gap={0}>
            <Tabs defaultValue="details" color="orange">
              <Tabs.List className={styles.bookingDrawerTabList}>
                <Tabs.Tab value="details" className={styles.bookingDrawerTab}>
                  Details
                </Tabs.Tab>
              </Tabs.List>
              <Tabs.Panel value="details" pt="sm">
                <Stack gap={0}>
                  {EMBEDDED_BOOKING_DRAWER_FORM.map((fld) => (
                    <div key={fld.key} className={styles.bookingDrawerRow}>
                      <Text component="span" className={styles.bookingDrawerLabel}>
                        {fld.label}
                      </Text>
                      <Box className={styles.bookingDrawerField}>{renderBookingDrawerValue(fld)}</Box>
                    </div>
                  ))}
                </Stack>
              </Tabs.Panel>
            </Tabs>
            <Divider my="md" />
            <Group justify="flex-end" align="center" wrap="wrap">
              <Button
                variant="Gradient"
                size="xs"
                onClick={handleSaveBooking}
                loading={drawerSaveLoading}
              >
                Save
              </Button>
              <Button variant="default" size="xs" onClick={handleCloseBookingDrawer}>
                Close
              </Button>
            </Group>
          </Stack>
        </Drawer>
      ) : null}

      <Paper shadow="sm" >
        <Modal opened={modalOpened} onClose={() => setModalOpened(false)} title="Create Booking"
          styles={{
            header: { paddingTop: 26, paddingBottom: 26 },
            title: {
              width: "100%",
              textAlign: "center",
              fontSize: "var(--mantine-font-size-md)", 
            },
          }}
          size="lg"
          centered
        >
        <Divider size={2}
          styles={{
            root: {
              border: "none",
              height: 2,
              background:
                "linear-gradient(270deg, #0053A8 0%, #033F7C 100%)",
              borderRadius: 8,
              opacity: 0.15,
            },
          }}
        />
          <Flex justify="center" direction="column" align="center" gap="lg" mt="xl" mb="lg">
            <Flex gap="md">
              <Button
                variant="outline2"
                leftSection={<IconLink />}
                onClick={() => navigate("/sales/leads/generate-link")}
              >
                Single Booking
              </Button>
              <Button
                variant="gradient"
                leftSection={<IconFileText />}
                onClick={() => navigate("/sales/leads/bulk-booking")}
              >
                Bulk Booking
              </Button>
            </Flex>
          </Flex>
        </Modal>
      </Paper>
    </Stack>
  );
};

export default Bookings;
